import { LoadingSpinner } from 'components/LoadingSpinner'
import { useMetadataUser } from 'contexts/metadata-user-context'
import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { IconType } from 'react-icons'
import { MdOutlineArrowForwardIos } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import {
  getWalletCompanyBalance,
  type WalletCompany as WalletCompanyType,
} from 'services/get-wallet-company'
import { MainTemplate } from 'templates/MainTemplate'
import convertNumbDote from 'utils/convertNumbDote'
import { Formatter } from 'utils/formatters'

import { CardWalletTransctionDetails } from './components/CardWalletTransctionDetails'

interface InfoCardProps {
  label: string
  value: string
  icon?: IconType
  onDetailsClick?: () => void
}

export type ActionType = 'credits' | 'debits'

function InfoCard({
  label,
  value,
  icon: Icon,
  onDetailsClick,
}: Readonly<InfoCardProps>) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <span className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
        {label}
      </span>
      <p className="mt-2 text-base font-seibold text-slate-900 dark:text-white break-words">
        {value}
      </p>

      {Icon && onDetailsClick && (
        <div
          className="flex items-center rounded-lg -ml-2 py-1 px-2 gap-1 w-1/6 cursor-pointer hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          onClick={onDetailsClick}
        >
          <span className="text-gray-500 text-sm">Ver mais</span>
          <span className="text-xs text-gray-500">{<Icon />}</span>
        </div>
      )}
    </div>
  )
}

export function WalletCompany() {
  const [loading, setLoading] = useState(true)
  const [selectedCard, setSelectedCard] = useState<ActionType | null>(null)
  const [walletCompanyBalance, setWalletCompanyBalance] =
    useState<WalletCompanyType | null>(null)

  const { darkMode, profile } = useMetadataUser()
  const navigate = useNavigate()

  useEffect(() => {
    if (profile && !profile.is_company_wallet_exists) {
      navigate('/home', { replace: true })
    }
  }, [navigate, profile])

  useEffect(() => {
    const fetchWalletCompanyBalance = async () => {
      setLoading(true)
      try {
        const data = await getWalletCompanyBalance()
        setWalletCompanyBalance(data)
      } catch {
        toast.error(
          'Erro ao buscar dados da carteira da empresa. Tente novamente mais tarde.',
        )
      } finally {
        setLoading(false)
      }
    }

    fetchWalletCompanyBalance()
  }, [])

  const companyInitials = useMemo(() => {
    if (!walletCompanyBalance?.trading_name && !walletCompanyBalance?.name) {
      return ''
    }

    const nameToFormat =
      walletCompanyBalance.trading_name ?? walletCompanyBalance.name

    return nameToFormat
      .split(' ')
      .filter((item) => Boolean(item))
      .slice(0, 2)
      .map((item) => item[0]?.toUpperCase())
      .join('')
  }, [walletCompanyBalance])

  const formattedBalance = useMemo(() => {
    if (!walletCompanyBalance) {
      return '--'
    }

    return convertNumbDote(walletCompanyBalance.balance)
  }, [walletCompanyBalance])

  const updatedAt = useMemo(() => {
    if (!walletCompanyBalance?.updated_at) {
      return '--'
    }

    return Formatter.formatterDateAndHour(walletCompanyBalance.updated_at)
      .hourAndDateFormateds
  }, [walletCompanyBalance])

  const formattedCNPJ = useMemo(() => {
    if (!walletCompanyBalance?.cnpj) {
      return '--'
    }
    return Formatter.prettyCNPJ(walletCompanyBalance.cnpj)
  }, [walletCompanyBalance])

  const formattedPurchaseTotal = useMemo(() => {
    if (!walletCompanyBalance) {
      return '--'
    }

    return convertNumbDote(walletCompanyBalance.purchase_total)
  }, [walletCompanyBalance])

  const formattedRewardTotal = useMemo(() => {
    if (!walletCompanyBalance) {
      return '--'
    }

    return convertNumbDote(walletCompanyBalance.reward_total)
  }, [walletCompanyBalance])

  const renderContent = () => {
    if (loading && !walletCompanyBalance) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-slate-300 p-10 text-slate-500 dark:border-slate-700 dark:text-slate-300">
          <LoadingSpinner />
          <span>Carregando dados da carteira...</span>
        </div>
      )
    }

    if (!walletCompanyBalance) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-3xl border border-dashed border-slate-300 p-10 text-center text-slate-500 dark:border-slate-700 dark:text-slate-300">
          <span className="text-base font-semibold">
            Não encontramos informações da carteira da empresa.
          </span>
          <span className="text-sm">
            Tente novamente mais tarde ou entre em contato com o suporte.
          </span>
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-4">
            {walletCompanyBalance.logo_url ? (
              <img
                src={walletCompanyBalance.logo_url}
                alt={`${walletCompanyBalance.trading_name} logo`}
                className="h-24 object-cover"
              />
            ) : (
              <div
                className={`flex h-16 w-16 items-center justify-center rounded-full text-lg font-semibold uppercase ${
                  darkMode
                    ? 'bg-slate-700 text-slate-200'
                    : 'bg-slate-200 text-slate-600'
                }`}
              >
                {companyInitials || 'CW'}
              </div>
            )}
            <div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
                Carteira da empresa
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {walletCompanyBalance.trading_name}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                Atualizado em {updatedAt}
              </p>
            </div>
          </div>

          <div className="flex flex-col items-start gap-2 md:items-end">
            <span className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
              Saldo disponível
            </span>
            <div className="flex items-center gap-2 text-3xl font-bold text-slate-900 dark:text-white">
              {formattedBalance}
              {loading ? <LoadingSpinner /> : null}
            </div>
            <span
              className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                walletCompanyBalance.active
                  ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                  : 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-300'
              }`}
            >
              {walletCompanyBalance.active
                ? 'Carteira ativa'
                : 'Carteira inativa'}
            </span>
          </div>
        </header>

        {!selectedCard ? (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <InfoCard
                label="Razão social"
                value={walletCompanyBalance.name}
              />
              <InfoCard
                label="Nome fantasia"
                value={walletCompanyBalance.trading_name}
              />
              <InfoCard label="CNPJ" value={formattedCNPJ} />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-2">
              <InfoCard
                label="TOTAL DE CRÉDITOS"
                value={formattedPurchaseTotal}
                icon={MdOutlineArrowForwardIos}
                onDetailsClick={() => setSelectedCard('credits')}
              />
              <InfoCard
                label="TOTAL DE DÉBITOS"
                value={formattedRewardTotal}
                icon={MdOutlineArrowForwardIos}
                onDetailsClick={() => setSelectedCard('debits')}
              />
            </div>
          </>
        ) : (
          <CardWalletTransctionDetails
            setSelectedCard={setSelectedCard}
            selectedCard={selectedCard}
          />
        )}
      </div>
    )
  }

  return (
    <MainTemplate>
      <section className="flex flex-1 flex-col gap-6 px-3 py-6 md:container md:py-10">
        {renderContent()}
      </section>
    </MainTemplate>
  )
}
